import React, { useEffect, useState } from 'react'
import {
  Box,
  Paper,
  Typography,
  Grid,
  FormControlLabel,
  Switch,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import {
  emailConfigService,
  EmailGlobalConfig,
  EmailGlobalConfigUpdate,
} from '../../services/emailConfigService'
import { getErrorMessage } from '../../utils/errorHandlers'

const ALERT_TOGGLES: Array<{ key: keyof EmailGlobalConfigUpdate; label: string; helperText: string }> = [
  { key: 'order_alerts_enabled', label: 'Order Alerts', helperText: 'Sales order lifecycle updates (confirmed, shipped, delivered, etc.)' },
  { key: 'payment_alerts_enabled', label: 'Payment Alerts', helperText: 'Payment received / invoice paid confirmations' },
  { key: 'inventory_alerts_enabled', label: 'Inventory Alerts', helperText: 'Internal low-stock alerts' },
  { key: 'shipment_alerts_enabled', label: 'Shipment Alerts', helperText: 'Shipping/tracking updates' },
  { key: 'document_alerts_enabled', label: 'Document Alerts', helperText: 'Estimates, invoices, delivery challans, purchase orders' },
  { key: 'promotional_alerts_enabled', label: 'Promotional Alerts', helperText: 'Marketing/follow-up messages' },
]

const EmailNotificationSettingsTab: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [config, setConfig] = useState<EmailGlobalConfig | null>(null)
  const [error, setError] = useState<string | null>(null)

  const loadConfig = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await emailConfigService.getGlobalConfig()
      setConfig(data)
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to load email notification settings'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadConfig()
  }, [])

  const handleToggle = (key: keyof EmailGlobalConfigUpdate) => {
    if (!config) return
    setConfig({ ...config, [key]: !config[key] } as EmailGlobalConfig)
  }

  const handleNumberChange = (key: keyof EmailGlobalConfigUpdate, value: string) => {
    if (!config) return
    const num = Number(value)
    if (Number.isNaN(num)) return
    setConfig({ ...config, [key]: num } as EmailGlobalConfig)
  }

  const handleSave = async () => {
    if (!config) return
    setSaving(true)
    try {
      const updated = await emailConfigService.updateGlobalConfig({
        order_alerts_enabled: config.order_alerts_enabled,
        payment_alerts_enabled: config.payment_alerts_enabled,
        inventory_alerts_enabled: config.inventory_alerts_enabled,
        shipment_alerts_enabled: config.shipment_alerts_enabled,
        document_alerts_enabled: config.document_alerts_enabled,
        promotional_alerts_enabled: config.promotional_alerts_enabled,
        max_messages_per_hour: config.max_messages_per_hour,
        min_delay_between_messages_seconds: config.min_delay_between_messages_seconds,
        max_retry_attempts: config.max_retry_attempts,
        default_language: config.default_language,
      })
      setConfig(updated)
      enqueueSnackbar('Email notification settings saved', { variant: 'success' })
    } catch (err: any) {
      enqueueSnackbar(getErrorMessage(err, 'Failed to save settings'), { variant: 'error' })
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    )
  }

  if (error || !config) {
    return <Alert severity="error">{error || 'No configuration available'}</Alert>
  }

  return (
    <Box>
      <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Alert Categories
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enable or disable email notifications by category.
        </Typography>
        <Grid container spacing={2}>
          {ALERT_TOGGLES.map((toggle) => (
            <Grid item xs={12} sm={6} key={toggle.key}>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(config[toggle.key])}
                    onChange={() => handleToggle(toggle.key)}
                  />
                }
                label={toggle.label}
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                {toggle.helperText}
              </Typography>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Rate Limiting
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              type="number"
              label="Max Messages / Hour"
              value={config.max_messages_per_hour}
              onChange={(e) => handleNumberChange('max_messages_per_hour', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              type="number"
              label="Min Delay Between Messages (seconds)"
              value={config.min_delay_between_messages_seconds}
              onChange={(e) => handleNumberChange('min_delay_between_messages_seconds', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              type="number"
              label="Max Retry Attempts"
              value={config.max_retry_attempts}
              onChange={(e) => handleNumberChange('max_retry_attempts', e.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      <Divider sx={{ mb: 3 }} />

      <Box display="flex" justifyContent="flex-end">
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>
    </Box>
  )
}

export default EmailNotificationSettingsTab
